import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';
import { formatDateTime } from '../utils/format';

const REFRESH_MS = 5000;
const FETCH_LIMIT = 200;

const EVENT_LABELS = {
  charger_connected: { label: 'Connected', kind: 'success' },
  charger_disconnected: { label: 'Disconnected', kind: 'neutral' },
  charger_reject_no_id: { label: 'Rejected: no ID', kind: 'reject' },
  charger_reject_bad_id: { label: 'Rejected: invalid ID', kind: 'reject' },
  charger_reject_not_whitelisted: { label: 'Rejected: not whitelisted', kind: 'reject' },
  charger_reject_no_subprotocol: { label: 'Rejected: no subprotocol', kind: 'reject' },
  charger_reject_subprotocol_mismatch: { label: 'Rejected: subprotocol mismatch', kind: 'reject' },
};

const ConnectionLog = () => {
  const { addToast } = useToast();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [chargerQuery, setChargerQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Avoid showing the spinner on background refreshes
  const initialLoadDone = useRef(false);

  const api = apiClient;

  const fetchEntries = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const response = await api.get('/audit-log', {
        params: { event_type_prefix: 'charger_', limit: FETCH_LIMIT },
      });
      const fresh = response.data.entries || [];
      // Keep the same array reference when the head id is unchanged so the table doesn't re-render every poll.
      setEntries(prev => (prev[0]?.id === fresh[0]?.id && prev.length === fresh.length ? prev : fresh));
      setLastUpdated(new Date());
    } catch (err) {
      if (!initialLoadDone.current) {
        addToast('Failed to fetch connection log: ' + (err.response?.data?.detail || err.message), 'error');
        setEntries([]);
      }
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [api, addToast]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      // Pause polling when the tab is hidden
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchEntries();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchEntries]);

  const filtered = useMemo(() => {
    let rows = entries;
    if (filter === 'reject') rows = rows.filter(e => e.event_type?.startsWith('charger_reject_'));
    else if (filter === 'connected') rows = rows.filter(e => e.event_type === 'charger_connected');
    else if (filter === 'disconnected') rows = rows.filter(e => e.event_type === 'charger_disconnected');
    const q = chargerQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter(e =>
        (e.target || '').toLowerCase().includes(q) ||
        (e.details || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [entries, filter, chargerQuery]);

  const eventBadge = (eventType) => {
    const meta = EVENT_LABELS[eventType] || { label: eventType, kind: 'neutral' };
    const colors = {
      success: { bg: '#d4edda', fg: '#155724' },
      reject: { bg: '#f8d7da', fg: '#721c24' },
      neutral: { bg: '#e2e3e5', fg: '#383d41' },
    };
    const c = colors[meta.kind];
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
      }}>
        {meta.label}
      </span>
    );
  };

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', width: '100%', padding: '0 0.75rem', boxSizing: 'border-box' },
    header: { marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
    title: { fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', margin: 0 },
    statusLine: { fontSize: '0.85rem', color: '#6c757d' },
    card: { backgroundColor: '#fff', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
    filterBar: { display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
    label: { fontSize: '0.8rem', fontWeight: 600, color: '#495057' },
    select: { padding: '0.45rem 0.6rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', minWidth: '140px' },
    btn: { padding: '0.45rem 1rem', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
    th: { textAlign: 'left', padding: '0.65rem 0.75rem', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.03em' },
    td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', color: '#333' },
    detailsCell: { fontFamily: 'monospace', fontSize: '0.82rem', color: '#555' },
    loadingText: { textAlign: 'center', padding: '2rem', color: '#6c757d', fontSize: '1rem' },
    emptyText: { textAlign: 'center', padding: '2rem', color: '#6c757d', fontSize: '0.95rem' },
    checkbox: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#495057', cursor: 'pointer' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Connection Log</h1>
        <div style={styles.statusLine}>
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : '—'}
          {autoRefresh && ' · auto-refresh on'}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.filterBar}>
          <label style={styles.label} htmlFor="filter">Show</label>
          <select
            id="filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All events</option>
            <option value="reject">Rejected only</option>
            <option value="connected">Connected only</option>
            <option value="disconnected">Disconnected only</option>
          </select>

          <label style={styles.label} htmlFor="chargerQuery">Charger / details</label>
          <input
            id="chargerQuery"
            type="search"
            value={chargerQuery}
            onChange={(e) => setChargerQuery(e.target.value)}
            placeholder="Filter by ID or IP"
            style={{ ...styles.select, minWidth: '180px' }}
          />

          <label style={styles.checkbox}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>

          <button onClick={fetchEntries} style={styles.btn}>Refresh now</button>
        </div>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.loadingText}>Loading connection log…</div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyText}>No connection events recorded yet.</div>
        ) : (
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ ...styles.table, minWidth: '760px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Charger ID</th>
                  <th style={styles.th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => (
                  <tr key={entry.id}>
                    <td style={styles.td}>{formatDateTime(entry.created_at)}</td>
                    <td style={styles.td}>{eventBadge(entry.event_type)}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{entry.target || '—'}</td>
                    <td style={{ ...styles.td, ...styles.detailsCell }}>{entry.details || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionLog;
